import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { subscriptionService } from '../../../services/subscriptionService';
import { useAuth } from '../../../contexts/AuthContext';
import SubscriptionCheckout from './SubscriptionCheckout';

const BillingSection = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usageMetrics, setUsageMetrics] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    if (user) {
      loadBillingData();
    }
  }, [user]);

  const loadBillingData = async () => {
    setLoading(true);
    try {
      const [plansData, subscriptionData, usageData, historyData] = await Promise.all([
        subscriptionService?.getPlans(),
        subscriptionService?.getCurrentSubscription(user?.id),
        subscriptionService?.getAllUsageMetrics(user?.id),
        subscriptionService?.getPaymentHistory(user?.id)
      ]);

      setPlans(plansData || []);
      setCurrentSubscription(subscriptionData);
      setUsageMetrics(usageData || {});
      setPaymentHistory(historyData || []);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  const handleCancelSubscription = async () => {
    if (!currentSubscription) return;
    
    if (window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      try {
        await subscriptionService?.cancelSubscription(currentSubscription?.id);
        await loadBillingData();
      } catch (error) {
        console.error('Failed to cancel subscription:', error);
        alert('Failed to cancel subscription. Please try again.');
      }
    }
  };

  const getUsagePercentage = (current, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((current / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-destructive';
    if (percentage >= 70) return 'bg-warning';
    return 'bg-success';
  };

  const formatLimit = (limit) => {
    return limit === -1 ? 'Unlimited' : limit;
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800',
      starter: 'bg-blue-100 text-blue-800',
      professional: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-amber-100 text-amber-800'
    };
    return colors?.[tier] || 'bg-gray-100 text-gray-800';
  };

  const isCurrentPlan = (plan) => {
    return currentSubscription?.plan?.id === plan?.id;
  };

  const canUpgrade = (plan) => {
    if (!currentSubscription) return plan?.tier !== 'free';
    const tierOrder = { free: 0, starter: 1, professional: 2, enterprise: 3 };
    const currentTier = tierOrder?.[currentSubscription?.plan?.tier] || 0;
    const planTier = tierOrder?.[plan?.tier] || 0;
    return planTier > currentTier;
  };

  const usageStats = [
    { 
      label: 'Connected Accounts', 
      current: usageMetrics?.brands_connected || 0, 
      limit: currentSubscription?.plan?.limits?.connected_accounts || 2, 
      icon: 'Link' 
    },
    { 
      label: 'Posts This Month', 
      current: usageMetrics?.posts_scheduled || 0, 
      limit: currentSubscription?.plan?.limits?.posts_per_month || 50, 
      icon: 'FileText' 
    },
    { 
      label: 'Team Members', 
      current: usageMetrics?.team_members || 1, 
      limit: currentSubscription?.plan?.limits?.team_members || 1, 
      icon: 'Users' 
    },
    { 
      label: 'Storage Used', 
      current: usageMetrics?.storage_gb || 0, 
      limit: currentSubscription?.plan?.limits?.storage_gb || 1, 
      icon: 'HardDrive', 
      unit: 'GB' 
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-1/4"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Subscription */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Current Subscription</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your subscription and billing information
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 p-6 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border border-primary/20">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-1">
                  {currentSubscription?.plan?.name || 'Free'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Billed {currentSubscription?.plan?.billing_interval || 'monthly'}
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-primary">
                  {subscriptionService?.formatCurrency(currentSubscription?.plan?.base_price || 0)}
                </div>
                <div className="text-xs text-muted-foreground">per {currentSubscription?.plan?.billing_interval || 'month'}</div>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              {currentSubscription?.plan?.features?.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {currentSubscription && (
              <div className="pt-4 border-t border-border space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium text-foreground capitalize">
                    {currentSubscription?.status}
                  </span>
                </div>
                {currentSubscription?.current_period_end && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {currentSubscription?.cancel_at_period_end ? 'Access until:' : 'Next billing date:'}
                    </span>
                    <span className="font-medium text-foreground">
                      {new Date(currentSubscription.current_period_end)?.toLocaleDateString()}
                    </span>
                  </div>
                )}
                {currentSubscription?.cancel_at_period_end && (
                  <div className="mt-2 p-2 bg-warning/10 border border-warning/20 rounded text-xs text-warning">
                    Your subscription will be canceled at the end of the billing period
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="md:w-64 space-y-3">
            {currentSubscription && !currentSubscription?.cancel_at_period_end && (
              <Button 
                variant="destructive" 
                fullWidth 
                iconName="X" 
                iconPosition="left"
                onClick={handleCancelSubscription}
              >
                Cancel Subscription
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Usage Statistics */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Usage Statistics</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor your current usage against plan limits
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {usageStats?.map((stat) => {
            const percentage = getUsagePercentage(stat?.current, stat?.limit);
            const formattedLimit = formatLimit(stat?.limit);
            return (
              <div key={stat?.label} className="p-4 border border-border rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={stat?.icon} size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-foreground">{stat?.label}</h3>
                    <p className="text-xs text-muted-foreground">
                      {stat?.current}{stat?.unit || ''} of {formattedLimit}{stat?.unit || ''}
                    </p>
                  </div>
                </div>
                {stat?.limit !== -1 && (
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getUsageColor(percentage)} transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Available Plans */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Available Plans</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Upgrade or downgrade your subscription anytime
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans?.map((plan) => {
            const isCurrent = isCurrentPlan(plan);
            const canUpgradeToPlan = canUpgrade(plan);
            
            return (
              <div 
                key={plan?.id} 
                className={`p-6 rounded-lg border-2 transition-all ${
                  isCurrent 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-foreground">{plan?.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTierBadgeColor(plan?.tier)}`}>
                      {plan?.tier?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{plan?.description}</p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-foreground">
                      {subscriptionService?.formatCurrency(plan?.base_price)}
                    </span>
                    <span className="text-sm text-muted-foreground">/{plan?.billing_interval}</span>
                  </div>
                </div>
                <div className="space-y-2 mb-6">
                  {plan?.features?.slice(0, 5)?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-foreground">
                      <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                  {plan?.features?.length > 5 && (
                    <p className="text-xs text-muted-foreground italic">
                      +{plan?.features?.length - 5} more features
                    </p>
                  )}
                </div>
                {isCurrent ? (
                  <Button variant="outline" fullWidth disabled>
                    Current Plan
                  </Button>
                ) : canUpgradeToPlan ? (
                  <Button 
                    fullWidth 
                    onClick={() => handleUpgrade(plan)}
                    iconName="ArrowUp"
                    iconPosition="right"
                  >
                    Upgrade
                  </Button>
                ) : (
                  <Button variant="outline" fullWidth disabled>
                    Not Available
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Payment History */}
      {paymentHistory?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground">Payment History</h2>
            <p className="text-sm text-muted-foreground mt-1">
              View your past transactions and invoices
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Description</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory?.map((transaction) => (
                  <tr key={transaction?.id} className="border-b border-border last:border-0">
                    <td className="py-3 px-4 text-sm text-foreground">
                      {new Date(transaction?.created_at)?.toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-sm text-foreground">
                      {transaction?.description}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-foreground">
                      {subscriptionService?.formatCurrency(transaction?.amount)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        transaction?.status === 'succeeded' ?'bg-success/10 text-success' 
                          : transaction?.status === 'failed' ?'bg-destructive/10 text-destructive' :'bg-muted text-muted-foreground'
                      }`}>
                        {transaction?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Checkout Modal */}
      {showCheckout && selectedPlan && (
        <SubscriptionCheckout
          plan={selectedPlan}
          isOpen={showCheckout}
          onClose={() => {
            setShowCheckout(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            loadBillingData();
          }}
        />
      )}
    </div>
  );
};

export default BillingSection;